import React, { FC, useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import Big from 'big.js';
import Plate from '@alfa-retail-react/components/plate';
import Button from '@alfa-retail-react/components/button';
import Drawer from '@alfa-retail-react/components/drawer';
import { ChevronRightMIcon } from '@alfalab/icons-glyph/ChevronRightMIcon';
import { CrossMIcon } from '@alfalab/icons-glyph/CrossMIcon';
import {
    AmountInput,
    Loader,
    Section,
    Tag,
    TooltipWithTriggerIcon,
    useFormContext,
} from '@alfa-retail-react/components';
import { useIsDesktop } from 'app/utils/useWindowType';
import { CalculatorProps } from 'app/service/types/creditCalculator';
import { LoadStateStore } from 'app/loading/LoadStateStore';
import { ValidationRules } from 'app/form/ValidationRules';
import { FormatUtils, getDecimalParts } from 'app/utils/FormatUtils';
import { useI18nContext } from 'app/store/сontext/I18nContext';

import './CreditCalculator.less';

const SUM_ITEM_NAME = 'sum';

interface ICreditCalculatorProps {
    loadState: LoadStateStore;
    sum: Big;
    creditCalculator: CalculatorProps;
    onChangeSum(sum: Big): void;
}

interface CalculatorFormValues {
    sum: Big;
}

const DEFAULT_CURRENCY = 'BYN';

const TERM_TYPE = {
    MONTH: 'мес',
};

export const CreditCalculator: FC<ICreditCalculatorProps> = ({
    loadState,
    sum,
    creditCalculator,
    onChangeSum,
}) => {
    const [isDesktop] = useIsDesktop();
    const { t } = useI18nContext();

    const [isOpen, setIsOpen] = useState(false);
    const [amount, setAmount] = useState(sum);
    const [itemIndex, setItemIndex] = useState(0);

    const currency = DEFAULT_CURRENCY;
    const maxValue = FormatUtils.toIntegerString(
        creditCalculator?.sum.maxValue,
    );
    const minValue = FormatUtils.toIntegerString(
        creditCalculator?.sum.minValue,
    );

    const { control, trigger, handleMultipleErrors, setFieldElement } =
        useFormContext<CalculatorFormValues>();

    const { field, fieldState } = useController<CalculatorFormValues>({
        name: SUM_ITEM_NAME,
        control,
        defaultValue: sum || 0,
        rules: {
            required: ValidationRules.required(),
            max: ValidationRules.max(
                creditCalculator?.sum.maxValue,
                t(['creditCalculator_panel_amount_max', maxValue, currency]),
            ),
            min: ValidationRules.min(
                creditCalculator?.sum.minValue,
                t(['creditCalculator_panel_amount_min', minValue, currency]),
            ),
        },
    });

    const handleOpenCalculator = (e: any) => {
        e.preventDefault();
        setIsOpen(true);
    };
    const handleCloseCalculator = () => setIsOpen(false);

    useEffect(() => {
        onChangeSum(amount);
    }, [amount]);

    useEffect(() => {
        const maxDurationIndex = creditCalculator?.items.length - 1;
        setItemIndex(maxDurationIndex);
    }, []);

    const calculatorButtonTitle = t('redCard_details_button_calculator');
    const calculatorButtonSubtitle = t('creditCalculator_panel_subtitle');
    const drawerTitle = t('creditCalculator_panel_title');
    const durationTitle = t('creditCalculator_panel_duration_title');
    const conditionsTitle = t('creditCalculator_panel_conditions_title');
    const plateContent = t([
        'creditCalculator_panel_plate_message',
        maxValue,
        currency,
    ]);
    const amountLabel = isDesktop
        ? t([
              'creditCalculator_panel_amount_label',
              minValue,
              maxValue,
              currency,
          ])
        : t('creditCalculator_panel_amount_label_short');
    const amountLength = maxValue.toString().replace(' ', '').length;
    const calculationDisclaimer = t(
        'creditCalculator_panel_calculator_disclaimer',
    );

    const handleSumChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        payload: {
            value: number | null;
            valueString: string;
        },
    ) => {
        let result;

        if (typeof payload.value === 'number') {
            result = (payload.value as number) / 100;

            setAmount(new Big(result));
        } else {
            result = '';
            setAmount(null);
        }

        field.onChange(result);
        trigger();
    };

    const minority = 100;
    const sumValue =
        typeof field.value === 'string'
            ? ''
            : (field.value as number) * minority;

    return (
        <div className="creditCalculator">
            <Button
                className="creditCalculatorButton"
                onClick={handleOpenCalculator}
                type="button"
            >
                <div className="buttonContent">
                    <div className="buttonContentSection">
                        <span className="title">{calculatorButtonTitle}</span>
                        <span className="subtitle">
                            {calculatorButtonSubtitle}
                        </span>
                    </div>
                    <ChevronRightMIcon />
                </div>
            </Button>
            <Drawer
                keepMounted
                className="creditCalculatorContent"
                open={isOpen}
                onBackdropClick={handleCloseCalculator}
                onClose={handleCloseCalculator}
            >
                <div className="titleSection">
                    {drawerTitle}
                    <Button
                        view="ghost"
                        size="xs"
                        className="contentClose"
                        onClick={handleCloseCalculator}
                    >
                        <CrossMIcon />
                    </Button>
                </div>
                <div className="infoSection">
                    <Plate view={'neutral'}>{plateContent}</Plate>
                </div>
                <div className="amountInputSection">
                    <AmountInput
                        ref={(element) => {
                            field.ref(element);
                            setFieldElement(SUM_ITEM_NAME, element);
                        }}
                        error={handleMultipleErrors(fieldState.error)}
                        size="l"
                        value={sumValue}
                        integerLength={amountLength}
                        label={amountLabel}
                        suffix={currency}
                        min={creditCalculator.sum.minValue}
                        max={creditCalculator.sum.maxValue}
                        minority={minority}
                        onBlur={field.onBlur}
                        onChange={handleSumChange}
                    />
                </div>
                <Section className="durationSelection" title={durationTitle}>
                    {creditCalculator.items.map((d, index) => (
                        <Tag
                            size="xxs"
                            shape="rectangular"
                            className="durationItem"
                            childrenClassName="durationItemContent"
                            checked={index === itemIndex}
                            key={d.term.value}
                            onClick={() => setItemIndex(index)}
                        >
                            {d.term.value} {TERM_TYPE[d.term.type]}
                        </Tag>
                    ))}
                </Section>
                <Section title={conditionsTitle}>
                    <ConditionsCalculated
                        isError={!!fieldState.error}
                        isDesktop={isDesktop}
                        isLoading={loadState.isLoading}
                        monthly={
                            creditCalculator.items[itemIndex].monthlyPayment
                                .amount
                        }
                        overpay={
                            creditCalculator.items[itemIndex].overpayment.amount
                        }
                        currency={DEFAULT_CURRENCY}
                        rate={
                            creditCalculator.items[itemIndex]
                                .overpaymentPercents.amount
                        }
                    />
                    <span className="calculationDisclaimer">
                        {calculationDisclaimer}
                    </span>
                </Section>
            </Drawer>
        </div>
    );
};

interface IConditionsCalculatedProps {
    overpay: number;
    monthly: number;
    currency: string;
    rate: number;
    isError: boolean;
    isDesktop: boolean;
    isLoading: boolean;
}

const ConditionsCalculated: FC<IConditionsCalculatedProps> = ({
    overpay,
    monthly,
    currency,
    rate,
    isError,
    isDesktop,
    isLoading,
}) => {
    const { t } = useI18nContext();
    const overpayValue = getDecimalParts(overpay, 2, true);
    const overpayLabel = t('creditCalculator_panel_overpay_title');
    const overpayTooltip = t([
        'creditCalculator_panel_overpay_tooltip',
        FormatUtils.toPercentString(rate),
    ]);
    const monthlyValue = getDecimalParts(monthly, 2, true);
    const monthlyLabel = t('creditCalculator_panel_monthlyPayment_title');
    const monthlyTooltip = t('creditCalculator_panel_monthlyPayment_tooltip');

    return (
        <div className="conditionsCalculated">
            <div className="conditionsItem">
                <div className="description">
                    {overpayLabel}
                    <TooltipWithTriggerIcon
                        isDesktop={isDesktop}
                        content={overpayTooltip}
                    />
                </div>
                <Loader show={isLoading} />
                {!isLoading && !isError && (
                    <>
                        <span className="value">
                            {overpayValue[0]}
                            {overpayValue[1] !== undefined && (
                                <span className="decimal">
                                    ,{overpayValue[1]}
                                </span>
                            )}
                        </span>
                        <span className="currency">{currency}</span>
                    </>
                )}
                {!isLoading && isError && <span className="currency">-</span>}
            </div>
            <div className="conditionsItem">
                <div className="description">
                    {monthlyLabel}
                    <TooltipWithTriggerIcon
                        isDesktop={isDesktop}
                        content={monthlyTooltip}
                    />
                </div>
                <Loader show={isLoading} />
                {!isLoading && !isError && (
                    <>
                        <span className="value">
                            {monthlyValue[0]}
                            {monthlyValue[1] !== undefined && (
                                <span className="decimal">
                                    ,{monthlyValue[1]}
                                </span>
                            )}
                        </span>
                        <span className="currency">{currency}</span>
                    </>
                )}
                {!isLoading && isError && <span className="currency">-</span>}
            </div>
        </div>
    );
};
